/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

import { RecordingOptionListType } from "./seriesRecordingOptions.types";
import type {
  ActiveOptionState,
  RecordingOption,
  SeriesRecordingDismissReason,
  SeriesRecordingFocus,
  SeriesRecordingMode,
  SeriesRecordingOptionsConfig,
  SeriesRecordingOptionsState
} from "./seriesRecordingOptions.types";
import { UnityRootState } from "../../UnityAppStore";
import { Locale, LocaleKeys } from "../../locale";

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Required by CodingGuidelines §7 (D-09)
const TAG: string = "seriesRecordingOptionsSlice";

/** Index of the Save action within the "actions" zone. */
export const SAVE_ACTION_INDEX: number = 0;
/** Index of the Cancel action within the "actions" zone. */
export const CANCEL_ACTION_INDEX: number = 1;

const INITIAL_FOCUS: SeriesRecordingFocus = { zone: "rows", index: 0 };

const INITIAL_ACTIVE_OPTION: ActiveOptionState = {
  optionListType: "",
  focusIndex: 0,
  pendingIndex: 0,
  actionFocusIndex: null
};

const INITIAL_STATE: SeriesRecordingOptionsState = {
  isVisible: false,
  title: "",
  ucid: "",
  mode: "modify",
  options: [],
  saveButtonLabel: "",
  cancelButtonLabel: "",
  focus: INITIAL_FOCUS,
  activatedOptionId: "",
  dismissReason: "",
  activeOption: INITIAL_ACTIVE_OPTION
};

const seriesRecordingOptionsSlice = createSlice({
  name: "seriesRecordingOptions",
  initialState: INITIAL_STATE,
  reducers: {
    /**
     * Show the overlay with the given configuration. Focus starts on the
     * first row, or on `initialFocusedOptionId` when it matches an option.
     */
    showSeriesRecordingOptions(
      state,
      action: PayloadAction<SeriesRecordingOptionsConfig>
    ): void {
      const { title, ucid, mode, options, saveButtonLabel, cancelButtonLabel } =
        action.payload;
      state.isVisible = true;
      state.title = title;
      state.ucid = ucid || "";
      state.mode = mode === undefined ? "modify" : mode;
      state.options = options;
      state.saveButtonLabel = saveButtonLabel;
      state.cancelButtonLabel = cancelButtonLabel;
      state.activatedOptionId = "";
      state.dismissReason = "";

      const initialId = action.payload.initialFocusedOptionId;
      const initialIndex =
        initialId === undefined
          ? -1
          : options.findIndex(
              (option: RecordingOption): boolean =>
                option.optionListType === initialId
            );
      state.focus =
        initialIndex >= 0
          ? { zone: "rows", index: initialIndex }
          : INITIAL_FOCUS;
    },

    /**
     * Dismiss the overlay, recording whether it was saved or cancelled.
     */
    dismissSeriesRecordingOptions(
      state,
      action: PayloadAction<SeriesRecordingDismissReason>
    ): void {
      state.isVisible = false;
      state.dismissReason = action.payload;
    },

    /** Reset the overlay state to initial values. */
    resetSeriesRecordingOptions(state): void {
      state.isVisible = INITIAL_STATE.isVisible;
      state.title = INITIAL_STATE.title;
      state.ucid = INITIAL_STATE.ucid;
      state.mode = INITIAL_STATE.mode;
      state.options = INITIAL_STATE.options;
      state.saveButtonLabel = INITIAL_STATE.saveButtonLabel;
      state.cancelButtonLabel = INITIAL_STATE.cancelButtonLabel;
      state.focus = INITIAL_STATE.focus;
      state.activatedOptionId = INITIAL_STATE.activatedOptionId;
      state.dismissReason = INITIAL_STATE.dismissReason;
      state.activeOption = INITIAL_STATE.activeOption;
    },

    /**
     * Activate an option row (opens its value picker in the real flow) and
     * sync focus to it — used by both D-pad OK and pointer clicks.
     */
    activateOption(
      state,
      action: PayloadAction<RecordingOptionListType>
    ): void {
      const index = state.options.findIndex(
        (option: RecordingOption): boolean =>
          option.optionListType === action.payload
      );
      if (index >= 0) {
        state.activatedOptionId = action.payload;
        state.focus = { zone: "rows", index };
      }
    },

    /**
     * Cycle the selected value of an option forward or backward through its
     * `horizontalOptions` list and update the displayed `value` string.
     * Disabled options are silently ignored.
     */
    cycleOptionValue(
      state,
      action: PayloadAction<{
        optionListType: RecordingOptionListType;
        direction: "next" | "prev";
      }>
    ): void {
      const { optionListType, direction } = action.payload;
      const option = state.options.find(
        (o: RecordingOption): boolean => o.optionListType === optionListType
      );
      if (
        option === undefined ||
        option.disabled === true ||
        option.horizontalOptions === undefined ||
        option.horizontalOptions.length === 0
      ) {
        return;
      }
      const total: number = option.horizontalOptions.length;
      const current: number = option.selectedIndex || 0;
      const next: number =
        direction === "next"
          ? (current + 1) % total
          : (current - 1 + total) % total;
      option.selectedIndex = next;
      const nextValue = option.horizontalOptions[next];
      if (nextValue !== undefined) {
        option.value = nextValue;
      }
    },

    /** Move D-pad focus up (rows clamp; actions return to the first row). */
    focusUp(state): void {
      if (state.focus.zone === "rows") {
        state.focus.index = Math.max(0, state.focus.index - 1);
      } else {
        state.focus = { zone: "rows", index: 0 };
      }
    },

    /** Move D-pad focus down (rows advance; last row moves to Save action; actions return to first row). */
    focusDown(state): void {
      if (state.focus.zone === "rows") {
        if (state.focus.index >= state.options.length - 1) {
          state.focus = { zone: "actions", index: SAVE_ACTION_INDEX };
        } else {
          state.focus.index = state.focus.index + 1;
        }
      } else {
        state.focus = { zone: "rows", index: 0 };
      }
    },

    /** Move D-pad focus left (Cancel→Save, Save→rows; rows are a no-op). */
    focusLeft(state): void {
      if (state.focus.zone !== "actions") {
        return;
      }
      state.focus =
        state.focus.index === SAVE_ACTION_INDEX
          ? { zone: "rows", index: 0 }
          : { zone: "actions", index: SAVE_ACTION_INDEX };
    },

    /** Move D-pad focus right (rows→Save, Save→Cancel; Cancel clamps). */
    focusRight(state): void {
      state.focus =
        state.focus.zone === "rows"
          ? { zone: "actions", index: SAVE_ACTION_INDEX }
          : {
              zone: "actions",
              index: Math.min(CANCEL_ACTION_INDEX, state.focus.index + 1)
            };
    },

    /** Directly set focus by zone and index */
    setFocus(state, action: PayloadAction<SeriesRecordingFocus>): void {
      state.focus = action.payload;
    },

    /**
     * Open the value selector for the given option, pre-focusing
     * the option's current selectedIndex.
     */
    openOptionValue(
      state,
      action: PayloadAction<RecordingOptionListType>
    ): void {
      const option = state.options.find(
        (o: RecordingOption): boolean => o.optionListType === action.payload
      );
      const initialIndex: number =
        option !== undefined && option.selectedIndex !== undefined
          ? option.selectedIndex
          : 0;
      state.activeOption = {
        optionListType: action.payload,
        focusIndex: initialIndex,
        pendingIndex: initialIndex,
        actionFocusIndex: null
      };
    },

    /** Close the value selector without saving the focused selection. */
    closeOptionValue(state): void {
      state.activeOption = INITIAL_ACTIVE_OPTION;
    },

    /** Move focus within the open value selector (clears action button focus). */
    setActiveOptionFocusIndex(state, action: PayloadAction<number>): void {
      state.activeOption.focusIndex = action.payload;
      state.activeOption.actionFocusIndex = null;
    },

    /**
     * Tick the currently focused item: move pendingIndex to focusIndex without
     * closing the selector. Used by VerticalListPicker on NAV_OK / item click.
     */
    tickActiveOptionFocus(state): void {
      state.activeOption.pendingIndex = state.activeOption.focusIndex;
    },

    /** Focus an action button in the VerticalListPicker (0 = OK, 1 = Cancel, null = list). */
    focusActiveOptionAction(state, action: PayloadAction<number | null>): void {
      state.activeOption.actionFocusIndex = action.payload;
    },

    /**
     * Confirm the focused value: write it back to the option's
     * selectedIndex and value, then close the selector.
     */
    confirmActiveOptionValue(state): void {
      const { optionListType, focusIndex } = state.activeOption;
      if (optionListType === "") {
        return;
      }
      const option = state.options.find(
        (o: RecordingOption): boolean => o.optionListType === optionListType
      );
      if (
        option !== undefined &&
        option.horizontalOptions !== undefined &&
        option.horizontalOptions.length > 0
      ) {
        option.selectedIndex = focusIndex;
        const focusedValue = option.horizontalOptions[focusIndex];
        if (focusedValue !== undefined) {
          option.value = focusedValue;
        }
      }
      state.activeOption = INITIAL_ACTIVE_OPTION;
    },

    /**
     * Commit the pending tick in the VerticalListPicker: write pendingIndex back
     * to the option's selectedIndex and close the selector.
     * Used by the OK button. Cancel uses closeOptionValue instead.
     */
    commitPendingActiveOptionValue(state): void {
      const { optionListType, pendingIndex } = state.activeOption;
      if (optionListType === "") {
        return;
      }
      const option = state.options.find(
        (o: RecordingOption): boolean => o.optionListType === optionListType
      );
      if (
        option !== undefined &&
        option.horizontalOptions !== undefined &&
        option.horizontalOptions.length > 0
      ) {
        option.selectedIndex = pendingIndex;
        const pendingValue = option.horizontalOptions[pendingIndex];
        if (pendingValue !== undefined) {
          option.value = pendingValue;
        }
      }
      state.activeOption = INITIAL_ACTIVE_OPTION;
    }
  }
});

/* ── Actions ──────────────────────────────────────────────────────────── */
export const {
  showSeriesRecordingOptions,
  dismissSeriesRecordingOptions,
  resetSeriesRecordingOptions,
  activateOption,
  cycleOptionValue,
  focusUp,
  focusDown,
  focusLeft,
  focusRight,
  setFocus,
  openOptionValue,
  closeOptionValue,
  setActiveOptionFocusIndex,
  tickActiveOptionFocus,
  focusActiveOptionAction,
  confirmActiveOptionValue,
  commitPendingActiveOptionValue
} = seriesRecordingOptionsSlice.actions;

/* ── Selectors ────────────────────────────────────────────────────────── */
export const selectIsSeriesRecordingOptionsVisible = (
  state: UnityRootState
): boolean => state.seriesRecordingOptions.isVisible;

export const selectSeriesRecordingOptionsTitle = (
  state: UnityRootState
): string => state.seriesRecordingOptions.title;

export const selectSeriesRecordingOptions = (
  state: UnityRootState
): RecordingOption[] => state.seriesRecordingOptions.options;

export const selectSeriesRecordingUcid = (state: UnityRootState): string =>
  state.seriesRecordingOptions.ucid;

export const selectSeriesRecordingMode = (
  state: UnityRootState
): SeriesRecordingMode => state.seriesRecordingOptions.mode;

export const selectSeriesRecordingSaveLabel = (state: UnityRootState): string =>
  state.seriesRecordingOptions.saveButtonLabel;

export const selectSeriesRecordingCancelLabel = (
  state: UnityRootState
): string => state.seriesRecordingOptions.cancelButtonLabel;

export const selectSeriesRecordingFocus = (
  state: UnityRootState
): SeriesRecordingFocus => state.seriesRecordingOptions.focus;

export const selectSeriesRecordingActivatedOptionId = (
  state: UnityRootState
): string => state.seriesRecordingOptions.activatedOptionId;

export const selectSeriesRecordingDismissReason = (
  state: UnityRootState
): SeriesRecordingDismissReason => state.seriesRecordingOptions.dismissReason;

export const selectIsActiveOptionVisible = (state: UnityRootState): boolean =>
  state.seriesRecordingOptions.activeOption.optionListType !== "";

export const selectActiveOptionFocusIndex = (state: UnityRootState): number =>
  state.seriesRecordingOptions.activeOption.focusIndex;

export const selectActiveOptionActionFocusIndex = (
  state: UnityRootState
): number | null => state.seriesRecordingOptions.activeOption.actionFocusIndex;

export const selectActiveOptionPendingIndex = (state: UnityRootState): number =>
  state.seriesRecordingOptions.activeOption.pendingIndex;

/** Returns the full RecordingOption that is currently active. */
export const selectActiveRecordingOption = (
  state: UnityRootState
): RecordingOption | undefined => {
  const { optionListType } = state.seriesRecordingOptions.activeOption;
  if (optionListType === "") {
    return undefined;
  }
  return state.seriesRecordingOptions.options.find(
    (o: RecordingOption): boolean => o.optionListType === optionListType
  );
};

export const selectActiveOptionDescription = (
  state: UnityRootState
): string => {
  const { optionListType } = state.seriesRecordingOptions.activeOption;

  switch (optionListType) {
    case RecordingOptionListType.Channel:
      return Locale.getString(
        LocaleKeys.MY_HUB_SERIES_RECORDING_OPTIONS_CHANNELS_DESCRIPTION
      );

    case RecordingOptionListType.KeepAtMost:
      return Locale.getString(
        LocaleKeys.MY_HUB_SERIES_RECORDING_OPTIONS_KEEP_AT_MOST_DESCRIPTION
      );

    case RecordingOptionListType.StartRecording:
      return Locale.getString(
        LocaleKeys.MY_HUB_SERIES_RECORDING_OPTIONS_START_RECORDING_DESCRIPTION
      );

    case RecordingOptionListType.StopRecording:
      return Locale.getString(
        LocaleKeys.MY_HUB_SERIES_RECORDING_OPTIONS_STOP_RECORDING_DESCRIPTION
      );

    default:
      return "";
  }
};

export default seriesRecordingOptionsSlice.reducer;
